import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { burstsOf, tokensFor, type FlowBurst, type FlowBursts } from './flow-motion';
import type { AdminFlow } from './flow-types';

/** The steps the screen has not drawn yet, per place; nothing on the first look. */
export function useFlowBursts(flow: AdminFlow): FlowBursts {
  const drawn = useRef<string | null>(null);
  const [bursts, setBursts] = useState<FlowBursts>({});
  useEffect(() => {
    if (drawn.current === flow.tick.end) return;
    setBursts(burstsOf(drawn.current, flow));
    drawn.current = flow.tick.end;
  }, [flow]);
  return bursts;
}

/** A burst's marks spread over most of a step, so a steady stream looks steady. */
const SPREAD_MS = 3600;

/**
 * The track from one place to the next. Its line is lit while pages left the place in the last minute, and
 * each burst sends marks across: one for a page, more as the pages double. With reduced motion the marks
 * stay off and the pages that moved are written on the track instead.
 */
export function FlowTrack({
  burst,
  live,
  className,
}: {
  burst: FlowBurst | undefined;
  live: boolean;
  className?: string;
}): ReactNode {
  const tokens = burst ? tokensFor(burst.pages) : 0;
  const classes = ['fl-track', live && 'fl-track--live', className].filter(Boolean).join(' ');
  return (
    <span className={classes} aria-hidden="true">
      <span className="fl-track__line" />
      {burst
        ? Array.from({ length: tokens }, (_, i) => (
            <i
              key={`${burst.key}-${i}`}
              className="fl-token"
              style={{ '--fl-delay': `${Math.round((i * SPREAD_MS) / tokens)}ms` } as CSSProperties}
            />
          ))
        : null}
      {burst ? (
        <span key={burst.key} className="fl-track__plus">
          +{burst.pages}
        </span>
      ) : null}
    </span>
  );
}
